import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  platforms: string[];
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  prompt(): Promise<void>;
}

interface PWAState {
  isOnline: boolean;
  isInstallable: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  installPrompt: BeforeInstallPromptEvent | null;
}

export const usePWA = () => {
  const [pwaState, setPwaState] = useState<PWAState>({
    isOnline: true,
    isInstallable: false,
    isInstalled: false,
    isStandalone: false,
    installPrompt: null
  });

  // Check if app is installed/standalone
  const checkStandalone = useCallback(() => {
    if (typeof window !== 'undefined') {
      const isStandalone = 
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone ||
        document.referrer.includes('android-app://');
      
      setPwaState(prev => ({ ...prev, isStandalone, isInstalled: isStandalone }));
    }
  }, []);

  // Handle online/offline status
  const updateOnlineStatus = useCallback(() => {
    setPwaState(prev => ({ ...prev, isOnline: navigator.onLine }));
  }, []);

  // Handle install prompt
  const handleBeforeInstallPrompt = useCallback((event: BeforeInstallPromptEvent) => {
    event.preventDefault();
    setPwaState(prev => ({ 
      ...prev, 
      isInstallable: true, 
      installPrompt: event 
    }));
  }, []);

  // Install the PWA
  const installPWA = useCallback(async () => {
    if (pwaState.installPrompt) {
      try {
        await pwaState.installPrompt.prompt();
        const choiceResult = await pwaState.installPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          console.log('PWA installation accepted');
          setPwaState(prev => ({ 
            ...prev, 
            isInstallable: false, 
            isInstalled: true,
            installPrompt: null 
          }));
        } else {
          console.log('PWA installation dismissed');
        }
      } catch (error) {
        console.error('Error installing PWA:', error);
      }
    }
  }, [pwaState.installPrompt]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (process.env.NODE_ENV !== 'production') {
      return; // Avoid service worker registration in development to prevent stale caches
    }

    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered:', registration);
        
        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available
                console.log('New version available');
                if (confirm('A new version is available. Reload to update?')) {
                  window.location.reload();
                }
              }
            });
          }
        });
        
        return registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return false;
  }, []);

  // Show notification
  const showNotification = useCallback(async (title: string, options?: NotificationOptions) => {
    if ('serviceWorker' in navigator && 'Notification' in window) {
      const registration = await navigator.serviceWorker.ready;
      if (Notification.permission === 'granted') {
        await registration.showNotification(title, {
          icon: '/favicon-96x96.png',
          badge: '/favicon-96x96.png',
          ...options
        });
      }
    }
  }, []);

  // Cache important data for offline use
  const cacheOfflineData = useCallback(async (data: any, key: string) => {
    if ('caches' in window) {
      try {
        const cache = await caches.open('datamapper-offline-data');
        const response = new Response(JSON.stringify(data));
        await cache.put(`/offline-data/${key}`, response);
      } catch (error) {
        console.error('Error caching offline data:', error);
      }
    }
  }, []);

  // Retrieve cached data when offline
  const getCachedData = useCallback(async (key: string) => {
    if ('caches' in window) {
      try {
        const cache = await caches.open('datamapper-offline-data');
        const response = await cache.match(`/offline-data/${key}`);
        if (response) {
          return await response.json();
        }
      } catch (error) {
        console.error('Error retrieving cached data:', error);
      }
    }
    return null;
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Initial checks
      checkStandalone();
      updateOnlineStatus();

      // Event listeners
      window.addEventListener('online', updateOnlineStatus);
      window.addEventListener('offline', updateOnlineStatus);
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);

      // Register service worker
      registerServiceWorker();

      // Check for display mode changes
      window.matchMedia('(display-mode: standalone)').addEventListener('change', checkStandalone);

      return () => {
        window.removeEventListener('online', updateOnlineStatus);
        window.removeEventListener('offline', updateOnlineStatus);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
        window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkStandalone);
      };
    }
  }, [checkStandalone, updateOnlineStatus, handleBeforeInstallPrompt, registerServiceWorker]);

  return {
    ...pwaState,
    installPWA,
    requestNotificationPermission,
    showNotification,
    cacheOfflineData,
    getCachedData
  };
};
